// src/pages/IntakeHub/IntakeHub.js
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import useCaseHistoryEnabled from '../../hooks/useCaseHistoryEnabled';
import NewChildSheet from './NewChildSheet';
import ExistingChildSheet from './ExistingChildSheet';
import { listCases, listTherapists, ageLabel, SEGMENTS } from './caseHistoryApi';
import { listPendingWithdrawals } from '../ConsentPage/consentApi';
import { captureError, openSession } from '../CapturePage/captureApi';
import '../../components/styles.css';
import './IntakeHub.css';

const FILTERS = [
    { key: 'all', label: 'All', match: () => true },
    { key: 'mine', label: 'Needs me', match: (c) => c.status === 'draft' },
    { key: 'therapist', label: 'Waiting on therapist', match: (c) => c.status === 'submitted' || c.status === 'active' },
    { key: 'done', label: 'Complete', match: (c) => c.status === 'locked' },
];

function CaseCard({ item, withdrawalPending }) {
    const navigate = useNavigate();
    const [starting, setStarting] = useState(false);
    const [startError, setStartError] = useState('');

    // Opening a session is idempotent on the server: an examiner who taps twice,
    // or comes back after the tablet slept, lands in the session they left open.
    const startSession = async () => {
        setStartError('');
        setStarting(true);
        try {
            const r = await openSession(item.id);
            navigate(`/intake/capture/${r.data.id}`);
        } catch (e) {
            setStartError(captureError(e, 'Could not open a recording session.'));
            setStarting(false);
        }
    };

    return (
        <div className={`ih-card ${item.status === 'draft' ? 'mine' : ''}`}>
            <div className="ih-row">
                <h3>{item.child_name}</h3>
                <span className={`ih-status ${item.status}`}>{item.status}</span>
            </div>
            <div className="ih-sub">
                {ageLabel(item.child_age_months)} · Episode {item.episode_number}
                {item.supervising_therapist_name ? ` · ${item.supervising_therapist_name}` : ''}
            </div>
            <div>
                <div className="ih-segs">
                    {SEGMENTS.map(([key]) => (
                        <span key={key} className={item.progress?.[key] ? 'done' : ''} />
                    ))}
                </div>
                <div className="ih-seg-labels">
                    {SEGMENTS.map(([key, label]) => <span key={key}>{label}</span>)}
                </div>
            </div>
            {!item.dob_verified && (
                <span className="ih-status warn" style={{ alignSelf: 'flex-start' }}>Date of birth not confirmed</span>
            )}
            <div className="ih-wait">Waiting on: <strong>{item.waiting_on}</strong></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* The consent screen stays reachable after it is signed. It holds what
                    the family agreed to, and it is where a withdrawal is raised — the
                    family tells the intake worker, so she must be able to get back to
                    it without asking anyone. */}
                {item.status !== 'locked' && (
                    <Link
                        to={`/intake/consent/${item.id}`}
                        className={`btn ${item.progress?.consent || withdrawalPending ? 'btn-secondary' : 'btn-primary'}`}
                        style={{ textDecoration: 'none' }}
                    >
                        {withdrawalPending ? 'View withdrawal'
                            : item.progress?.consent ? 'View consent' : 'Take consent'}
                    </Link>
                )}
                <Link to={`/intake/case-history/${item.id}`} className="btn btn-secondary" style={{ textDecoration: 'none' }}>
                    {item.status === 'draft' ? 'Open form' : 'View form'}
                </Link>
                {item.progress?.consent && item.status !== 'locked' && !withdrawalPending && (
                    <button type="button" className="btn btn-secondary" onClick={startSession} disabled={starting}>
                        {starting ? 'Opening…' : 'Start recording session'}
                    </button>
                )}
            </div>
            {startError ? <p className="ih-note red">{startError}</p> : null}
            {withdrawalPending ? (
                // Said here rather than only on the consent screen: this card is
                // where she would otherwise tap "Start recording session" for a
                // child whose family has already asked to stop.
                <span className="ih-status warn" style={{ alignSelf: 'flex-start' }}>
                    Withdrawal requested — recording stopped
                </span>
            ) : item.progress?.consent && (
                <span className="ih-status locked" style={{ alignSelf: 'flex-start' }}>Consent signed</span>
            )}
        </div>
    );
}

export default function IntakeHub() {
    const { user, logout } = useAuth();
    const { enabled, loading: flagLoading } = useCaseHistoryEnabled();

    const [cases, setCases] = useState([]);
    const [withdrawing, setWithdrawing] = useState(new Set());
    const [therapists, setTherapists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [sheet, setSheet] = useState(null); // 'new' | 'existing'
    const [flash, setFlash] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            listCases(),
            listTherapists(),
            // Which children are waiting on a withdrawal decision. A failure here
            // must not empty the hub, so it answers with nothing instead.
            listPendingWithdrawals().catch(() => ({ data: [] })),
        ])
            .then(([caseRes, therapistRes, pendingRes]) => {
                setCases(caseRes.data);
                setTherapists(therapistRes.data);
                setWithdrawing(new Set((pendingRes.data || []).map((r) => r.child_id)));
                setError('');
            })
            .catch(() => setError('Could not load your cases. Check your connection and try again.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (enabled) load();
    }, [enabled, load]);

    if (flagLoading) return <div className="ih-empty">Loading…</div>;

    if (!enabled) {
        return (
            <div className="therapist-page">
                <header className="page-header"><h1>Intake Hub</h1></header>
                <div className="section">
                    <div className="ih-note amber">Case History is not switched on for this installation yet.</div>
                </div>
            </div>
        );
    }

    const shown = cases.filter(FILTERS.find((f) => f.key === filter).match);
    const onCreated = (created) => {
        setSheet(null);
        setFlash(`${created.child_name} — episode ${created.episode_number} created as a draft.`);
        load();
    };

    return (
        <div className="therapist-page">
            <header className="page-header">
                <h1>Intake Hub</h1>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>Welcome back, {user?.username}</span>
                    <button
                        onClick={logout}
                        style={{ padding: '6px 14px', background: '#f44336', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                        Logout
                    </button>
                </div>
            </header>

            <div className="section">
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                        <h2 style={{ margin: 0 }}>My cases</h2>
                        <div className="ih-sub">Only cases you created.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-secondary" onClick={() => setSheet('existing')}>Start from existing child</button>
                        <button className="btn btn-primary" onClick={() => setSheet('new')}>+ New Child</button>
                    </div>
                </div>

                {flash && <div className="ih-note green" style={{ marginBottom: 12 }}>{flash}</div>}
                {error && <div className="ih-note red" style={{ marginBottom: 12 }}>{error}</div>}

                <div className="ih-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            className={`ih-chip ${filter === f.key ? 'active' : ''}`}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label} · {cases.filter(f.match).length}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="ih-empty">Loading cases…</div>
                ) : shown.length === 0 ? (
                    <div className="ih-empty">
                        {cases.length === 0 ? 'No cases yet — add a child to begin.' : 'No cases match this filter.'}
                    </div>
                ) : (
                    <div className="ih-cards">
                        {shown.map((item) => (
                            <CaseCard
                                key={item.id}
                                item={item}
                                withdrawalPending={withdrawing.has(item.child_id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {sheet === 'new' && (
                <NewChildSheet therapists={therapists} onClose={() => setSheet(null)} onCreated={onCreated} />
            )}
            {sheet === 'existing' && (
                <ExistingChildSheet therapists={therapists} onClose={() => setSheet(null)} onCreated={onCreated} />
            )}
        </div>
    );
}
